import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { enableProdMode } from '@angular/core';
import { AppModule } from './app/';
import { app as teamsApp } from '@microsoft/teams-js';
import { Util } from './app/utils/utils.module';

declare let Office;
const windowConfirmOriginal = window.confirm;
const replaceStateOriginal = window.history.replaceState;
const pushStateOriginal = window.history.pushState;
const microsoftOfficeScript = 'https://appsforoffice.microsoft.com/lib/1.1/hosted/office.debug.js';
const localOfficeScript = './assets/office-js/office.debug.js';
let officeLaunchNotSupported: string = null;

const startAngular = () => {
  enableProdMode();
  platformBrowserDynamic().bootstrapModule(AppModule);
};

const getUserLanguage = () => {
  if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
    return undefined;
  }
  const nav: any = window.navigator;
  let browserLang: any = (nav.languages && nav.languages.length > 0) ? nav.languages[0] : (nav.language || nav.browserLanguage || nav.userLanguage);
  if (browserLang) {
    if (browserLang.indexOf('-') !== -1) {
      browserLang = browserLang.split('-')[0];
    }
    if (browserLang.indexOf('_') !== -1) {
      browserLang = browserLang.split('_')[0];
    }
  }
  return browserLang;
};

const getRootSiteUrl = (): string => {
  const path: string = location.pathname;
  const pathParts: string[] = path.split('/');
  pathParts.splice(pathParts.length - 1, 1);
  return location.origin + pathParts.join('/');
};


const loadLang = (lang: string): Promise<void> => new Promise<void>((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  const url = getRootSiteUrl() + '/assets/i18n/' + lang + '.json';
  xhr.open('GET', url, true);
  xhr.onerror = reject;
  xhr.onreadystatechange = () => {
    if (xhr.readyState === 4) {
      if ((xhr.status === 200 || xhr.status === 0) && !!xhr.response) {
        localStorage.setItem('edx_lang_' + lang, xhr.response);
        resolve();
      } else {
        reject('no lang');
      }
    }
  };
  xhr.send();
});

const getEffectiveLanguage = () => {
  try {
    if (typeof Office !== 'undefined' && Office.context && Office.context.displayLanguage) {
      return Office.context.displayLanguage.split('-')[0]; // e.g., 'en-US' → 'en'
    }
  } catch {
    // Ignore Error
  }
  return getUserLanguage();
};

const loadLanguages = (): Promise<void> => {
  // choose language to use
  let currentLang = 'en';
  return new Promise<void>((resolve, reject) => {
    loadLang(currentLang).then(() => {
      localStorage.setItem('edx_curlang', currentLang);
      const userLang: string = getEffectiveLanguage();
      if (!!userLang) {
        loadLang(userLang).then(() => {
          currentLang = userLang;
          localStorage.setItem('edx_curlang', currentLang);
          resolve();
        }, err => {
          console.log(`Could not load: ${userLang} err: ${err}`);
          resolve();
        });
      } else {
        resolve();
      }
    }, reject);
  });
};

const launch = () => {
  loadLanguages().then(() => {
    startAngular();
  }, startAngular);
};

const noOfficeLaunch = () => {
  setTimeout(() => {
    (window as any).Office = undefined;
    Office = undefined;
    launch();
  }, 100);
};

const showOfficeOnlineNotSupportedMessage = async () => {
  if (!!officeLaunchNotSupported) {
    setOfficeNotSupportedMessage(officeLaunchNotSupported);
    return;
  }
  await loadLanguages();
  const lang = localStorage.getItem('edx_curlang');
  let msg = 'This add-in is not supported in Office Online. Please use the desktop version.';
  try {
    const jsonStr = localStorage.getItem('edx_lang_' + lang);
    if (!!jsonStr) {
      const json = JSON.parse(jsonStr);
      msg = json['OFFICE_LAUNCH_NOT_SUPPORTED'] || msg;
      officeLaunchNotSupported = msg;
    }
  } catch {
    // Ignore Error
  } finally {
    localStorage.removeItem('edx_lang_' + lang);
    localStorage.removeItem('edx_curlang');
  }
  setOfficeNotSupportedMessage(msg);
};

const setOfficeNotSupportedMessage = (msg: string): void => {
  document.body.innerHTML = `
    <div style="padding:1rem;">
      ${msg}
    </div>`;
};

const withOfficeLaunch = () => {
  checkIfFileExists(localOfficeScript).then(exists => {
    if (exists) {
      console.log('Loading local Office-js');
      loadOffice(localOfficeScript);
    } else {
      console.log('Loading Microsoft Office-js');
      loadOffice(microsoftOfficeScript);
    }
  });
};

const loadOffice = (officeScript) => {
  const script = document.createElement('script');
  script.onerror = (error) => {
    document.head.removeChild(script);
    noOfficeLaunch();
  };
  script.onload = () => {
    window.confirm = windowConfirmOriginal;
    window.history.replaceState = replaceStateOriginal;
    window.history.pushState = pushStateOriginal;
    if (Util.safeHasOwn(window, 'Office')) {
      // Application-specific initialization code goes into a function that is
      // assigned to the Office.initialize event and runs after the Office.js initializes.
      // Bootstrapping of the AppModule must come AFTER Office has initialized.
      Office.onReady().then(info => {
        if (!!info && !!info.platform) {
          if (info.platform === Office.PlatformType.OfficeOnline && info.host !== Office.HostType.Outlook) {
            showOfficeOnlineNotSupportedMessage();
            return;
          }
          launch();
        } else {
          noOfficeLaunch();
        }
      });
    } else {
      launch();
    }
  };
  script.src = officeScript;
  document.head.appendChild(script);
};

const checkIfFileExists = (url: string): Promise<boolean> => {
  return fetch(url, { method: 'HEAD', cache: 'no-cache' })
    .then(response => !!response.ok)
    .catch(() => false);
};

teamsApp.initialize().then(() => {
  noOfficeLaunch();
}, (err) => {
  withOfficeLaunch();
});
